// What the air actually feels like where a bird is standing (PLAN-04 S3,
// REFOCUS §7). The forecast's air temperature is measured in the shade
// (WTH-05), so on its own it understates the load a bird in the sun actually
// carries. This module turns the forecast plus a spot's shade into the
// temperature that reaches the bird, and lets each waterer find its own
// equilibrium with the spot it stands in.
//
// Everything here is a function of state and the hour. No clocks, no Math
// random: a replay of the same log reaches the same temperatures.

import { clamp } from '../core/math';
import type { GameState, Structure } from '../core/types';
import type { BiologyConfig } from './config';
import { getConfig } from './config';
import { hourOfDay } from './time';
import {
  fixturesOf,
  spotDef,
  spotDefs,
  spotIdOf,
  structureInSpot,
  type SpotDef,
} from './spots';

/** First of the 13 hours the shade tables cover (06:00). */
export const SHADE_START_HOUR = 6;

/** Hours the shade tables cover, 06:00 through 18:00. */
export const SHADE_HOURS = 13;

/**
 * 1 while the sun can be loading a spot, 0 outside the shade table's window.
 * The tables are defined for daylight, and there is no sun-exposed air to add at
 * night, so the solar terms fall away rather than persisting a 10 degF
 * increment into an 8 pm temperature.
 */
export function solarFactorAt(hour: number): number {
  const index = hour - SHADE_START_HOUR;
  return index >= 0 && index < SHADE_HOURS ? 1 : 0;
}

/**
 * How much of a spot's daylight the roof soffit blocks, clamped to the first and
 * last hour outside the table. The spot definitions own these numbers; this only
 * reads them at the hour.
 */
export function shadeAt(spot: SpotDef, hour: number): number {
  const index = Math.floor(hour) - SHADE_START_HOUR;
  if (index < 0) return spot.shade[0];
  if (index >= SHADE_HOURS) return spot.shade[SHADE_HOURS - 1];
  return spot.shade[index];
}

/** Forecast air temperature, which is what the weather source reports. */
export function ambientTempF(state: GameState): number {
  return state.weather.tempF;
}

/**
 * How much of the sun's radiant load reaches a bird standing inside under this
 * roof, as a share of the bare tin worst case. MAT-04 measures the roof
 * surface, not the air under it, so the load is the surface's excess over the
 * reference air, normalized against bare tin. A white-painted roof comes out
 * near zero because MAT-04 puts it barely above that reference air: white tin
 * is a heat sink, not just a weaker heater. Only the sun term scales with it -
 * a roof at night is at air temperature like everything else.
 */
export function roofRadiantShare(roof: ReturnType<typeof fixturesOf>['roof'], config: BiologyConfig = getConfig('normal')): number {
  const s = config.spots;
  const worst = s.roofUndersurfaceF.bare_tin - s.roofRadiantReferenceAirF;
  if (worst <= 0) return 1;
  const excess = s.roofUndersurfaceF[roof] - s.roofRadiantReferenceAirF;
  return clamp(excess / worst, 0, 1);
}

/**
 * The temperature a bird standing in this spot is carrying.
 *
 * - A yard spot is `air` in shade and `air + SUN_RADIANT_F x (1 - shade)` in
 *   sun (WTH-05's principle, the increment a design draft; WTH-08 is
 *   UNRESOLVED, so nothing here is sourced).
 * - An inside spot takes the roof's radiant load instead of a spot's own shade,
 *   which is why a bare-tin coop cooks its birds through a San Juan afternoon and
 *   a white one does not (MAT-04).
 * - A balcony sits in the urban heat island, +5 degF over the same air
 *   (WTH-02, 3.5-7 degF sourced).
 */
export function effectiveTempF(
  state: GameState,
  spot: SpotDef,
  hour: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  const s = config.spots;
  let temp = ambientTempF(state);
  const solar = solarFactorAt(hour);
  if (solar > 0) {
    if (spot.kind === 'inside') {
      temp += roofRadiantShare(fixturesOf(state).roof, config) * s.sunRadiantF;
    } else {
      temp += s.sunRadiantF * (1 - shadeAt(spot, hour));
    }
  }
  if (state.housing === 'balcony') temp += s.urbanOffsetF;
  return temp;
}

/**
 * The temperature a bird standing at this spot is carrying. A bird that is not
 * at any spot is in the open, so it gets the full sun increment.
 */
export function effectiveTempForSpot(
  state: GameState,
  spotId: string | undefined,
  hour: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  const spot = spotId ? spotDef(state.housing, spotId) : undefined;
  if (spot) return effectiveTempF(state, spot, hour, config);
  const s = config.spots;
  return ambientTempF(state) + (state.housing === 'balcony' ? s.urbanOffsetF : s.sunRadiantF * solarFactorAt(hour));
}

/**
 * The air at or above which the sim starts a bird on heat stress.
 *
 * Read from the season profile rather than typed as a number here, because it
 * is the same line `sim/disease.ts` uses to give a bird `heat_stress` and
 * `sim/physiology.ts` uses to push her core up. A warning that fires on some
 * other number than the one the sim acts on is a warning about the weather
 * rather than about the birds.
 */
export function heatWatchAirF(config: BiologyConfig = getConfig('normal')): number {
  return config.temperature.severeHeatF;
}

/**
 * Whether this yard is in a heat watch right now.
 *
 * The air it reads is the open yard's — `ambient + the sun's load` — because
 * that is what a bird standing in the sun is charged with, and it is the number
 * that crosses the severe-heat line. The forecast's own reading is shade air
 * (WTH-05), so a shade-air threshold sits about ten degrees below the moment
 * the sim actually hurts a bird.
 *
 * A pure function of the state, so a league save gets the league's answer on
 * every device and the server's replay reaches the same one: in a league
 * `state.weather` is the season's own (D7, REFOCUS §11), and in Normal it is
 * the town the player picked, which is the one thing Normal is a sandbox for.
 */
export function heatWatchActive(
  state: GameState,
  config: BiologyConfig = getConfig(state.veteran.enabled ? 'veteran' : 'normal'),
): boolean {
  return (
    effectiveTempForSpot(state, undefined, hourOfDay(state.simHour), config) >= heatWatchAirF(config)
  );
}

/**
 * The open spots a bird can stand in, coolest first, and how cool they are.
 * Spots within `SHADE_TIE_F` of the coolest count as equally good: shade is an
 * area, and a flock that all walks onto the single coolest tile is a modelling
 * artefact, not a behaviour.
 */
export function openSpotsByCoolth(
  state: GameState,
  hour: number,
  config: BiologyConfig = getConfig('normal'),
): Array<{ spot: SpotDef; tempF: number }> {
  const candidates = spotDefs(state.housing)
    // A spot with gear in it is where the bird goes to drink or eat, which is a
    // need; this is about where it stands out of the sun, so an empty one is
    // the useful answer.
    .filter((spot) => !structureInSpot(state, spot.id))
    .map((spot) => ({ spot, tempF: effectiveTempF(state, spot, hour, config) }));
  if (candidates.length === 0) return [];
  const coolest = Math.min(...candidates.map((c) => c.tempF));
  return candidates
    .filter((c) => c.tempF <= coolest + config.spots.shadeTieF)
    .sort((a, b) => a.tempF - b.tempF);
}

/**
 * The coolest open spot a bird can stand in, or null when the yard has nowhere
 * cooler. Hot birds walk here (PLAN-04 S3); S7 reads the same comparison for the
 * first visible sign.
 */
export function coolestSpot(
  state: GameState,
  hour: number,
  config: BiologyConfig = getConfig('normal'),
): { spot: SpotDef; tempF: number } | null {
  return openSpotsByCoolth(state, hour, config)[0] ?? null;
}

// --- drinker temperature -----------------------------------------------------

/** Which body of water a drinker is, which is what sets how fast it heats. */
export type WaterBody = 'fount' | 'hopper' | 'plumbed';

/**
 * Item id to water body. This is a classification, not a number: the heating
 * rates live in biology-config under spots.waterThermalTauHours, so a sourced
 * rate can replace a draft without touching this table.
 */
const WATER_BODY: Record<string, WaterBody> = {
  chick_waterer: 'fount',
  adult_waterer: 'hopper',
  auto_waterer: 'plumbed',
  water_barrel: 'hopper',
};

/**
 * The water body an item is, or null when it holds no water. A feeder is not a
 * slow drinker: it has no temperature to model, and defaulting it to a hopper
 * would give every piece of equipment a fake warm reading.
 */
export function waterBodyOf(itemId: string): WaterBody | null {
  return WATER_BODY[itemId] ?? null;
}

/** The temperature currently modelled for a drinker, or null when it has none. */
export function waterTempF(structure: Structure): number | undefined {
  const value = structure.state.waterTempF;
  return typeof value === 'number' ? value : undefined;
}

/** The temperature the drinker at this spot tends toward: its spot's air. */
export function waterEquilibriumF(
  state: GameState,
  structure: Structure,
  hour: number,
  config: BiologyConfig = getConfig('normal'),
): number {
  return effectiveTempForSpot(state, spotIdOf(state, structure.id), hour, config);
}

/**
 * Moves every drinker one step toward the air at its own spot. The step is
 * exponential with the container's time constant, so a fount tracks its spot
 * within the hour and a hopper lags it by hours (WTH-07 is UNRESOLVED, so the
 * constants are drafts).
 *
 * A drinker with no modelled temperature starts at its spot's current air: one
 * bought at two in the afternoon is already warm, and a water barrel filled by
 * the stock_water chore does not begin cold in a San Juan July.
 *
 * Nothing here is rounded. This is an accumulator, and a live Real-Time tick is
 * 1/3600 of an hour: against a one-hour time constant that is a movement of
 * 0.00028 degF, which `round1` turns straight back into the value it started
 * from. The drinker therefore never moved at all in live play — it was set once
 * when the player first stepped the yard and kept that temperature for the rest
 * of the save, while offline catch-up's quarter-hour steps carried it
 * correctly. That is the same rounding bug already fixed in bird ageing, the
 * world clock, litter soiling, water drain and body condition. Round at the
 * display edge, never on a value the next tick reads back.
 */
export function stepWaterTemperature(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
): void {
  if (hours <= 0) return;
  const hour = hourOfDay(state.simHour);
  for (const structure of state.structures) {
    const body = waterBodyOf(structure.itemId);
    if (!body) continue;
    const tau = config.spots.waterThermalTauHours[body];
    if (tau <= 0) continue;
    const target = effectiveTempForSpot(state, spotIdOf(state, structure.id), hour, config);
    const current = waterTempF(structure);
    if (current === undefined) {
      structure.state.waterTempF = target;
      continue;
    }
    const settled = 1 - Math.exp(-hours / tau);
    structure.state.waterTempF = current + (target - current) * settled;
  }
}

/**
 * The whole hourly microclimate pass. Deterministic and clock-free, so the same
 * log always produces the same water temperatures.
 */
export function stepMicroclimate(
  state: GameState,
  hours: number,
  config: BiologyConfig = getConfig('normal'),
): void {
  stepWaterTemperature(state, hours, config);
}
